import { isFulfilled, isRejectedWithValue } from '@reduxjs/toolkit'
import { Middleware } from 'redux'

import { avisoDelFalloRtk, necesitaAvisoGenerico, PayloadDeFallo } from './avisoDelFalloRtk'

import { addNotification } from '@/common/components/Notifications/state/notificationsState'
import {
  sendCourierErrorNotification,
  getCurrentUserId,
} from '@/common/utils/courierNotifications'
/**
 * Payload de un RECHAZO de RTK Query, que es un `FetchBaseQueryError`: el `status` es el codigo HTTP
 * —o una cadena como `FETCH_ERROR` en los fallos de transporte—.
 */
interface Payload {
  status: number
  data: { title?: string; detail: string }
}

/**
 * Payload de un CUMPLIMIENTO, que es OTRA COSA: es el CUERPO de la respuesta, no el sobre HTTP.
 *
 * La distincion importa y estaba perdida —las dos ramas compartian la interfaz `Payload`—: en el
 * camino de exito, `status` no es el codigo HTTP sino un campo que el backend puso DENTRO del cuerpo.
 * Los endpoints de negocio devuelven `{ status, data: { title, detail } }` y por eso el aviso
 * funciona con ellos; el resto devuelve `{ value: [...] }` u otra forma, donde `status` no existe y
 * no sale ningun aviso. O sea que quien decide si hay toast es la forma del cuerpo, no el HTTP.
 */
interface CuerpoConAviso {
  status?: number
  data?: { title?: string; detail?: string }
}

/**
 * Endpoints cuyo fallo ya se avisa en su propia pantalla, con un texto que dice mas que el
 * generico (por ejemplo: que la configuracion quedo solo en este navegador). El aviso generico los
 * saltea para que no salgan dos toasts por el mismo error.
 *
 * Antes de que existiera el aviso generico, la compensacion era al reves: cada pantalla filtraba a
 * mano los codigos que el middleware SI cubria. Eso se invirtio.
 */
const ENDPOINTS_CON_AVISO_PROPIO = new Set<string>([
  'updatedUserConfiguration',
  'createUserConfiguration',
  'assignAgenteOperativo',
])

const endpointDeLaAccion = (action: unknown): string | undefined =>
  (action as { meta?: { arg?: { endpointName?: string } } })?.meta?.arg?.endpointName

/** `true` solo para acciones de mutacion; las consultas no deben festejar nada. */
const esMutacion = (action: unknown): boolean =>
  (action as { meta?: { arg?: { type?: string } } })?.meta?.arg?.type === 'mutation'

// config api middleware
export const rtkQueryErrorLogger: Middleware =
  // El store sale del parametro que Redux le pasa a todo middleware. Antes se importaba desde
  // `./store`, que a su vez importa este archivo: un ciclo que funcionaba solo porque la lectura
  // ocurria en el dispatch, con el grafo de modulos ya resuelto. Moverla al scope del modulo, o un
  // reordenamiento del bundler, la dejaba en undefined.
  (store) => (next) => (action: unknown) => {
    if (isRejectedWithValue(action)) {
      const response = action.payload as Payload

      if (response.status === 401) {
        store.dispatch(
          addNotification({
            value: {
              message: response?.data?.detail,
              style: 'error',
              title: response?.data?.title ?? 'Reservas',
            },
          }),
        )

        return next(action)
      }

      if (response.status === 500) {
        store.dispatch(
          addNotification({
            value: {
              message: response?.data?.detail,
              style: 'error',
              title: response?.data?.title ?? 'Reservas',
            },
          }),
        )

        return next(action)
      }

      if (response.status === 400) {
        store.dispatch(
          addNotification({
            value: {
              message: response?.data?.detail,
              style: 'warning',
              title: response?.data?.title ?? 'Reservas',
            },
          }),
        )

        // Enviar notificación via Courier para errores 400
        const userId = getCurrentUserId()

        if (userId) {
          sendCourierErrorNotification({
            userId,
            errorMessage: response?.data?.detail || 'Error de validación',
            errorTitle: response?.data?.title ?? 'Error en Reservas',
            statusCode: 400,
          })
        }

        return next(action)
      }

      // Cualquier otro rechazo. Antes se perdia EN SILENCIO: solo habia ramas para 400, 401 y 500,
      // asi que un 403 por desfase de roles, un 404, un 409 o un fallo de red no producian ninguna
      // señal. Los de transporte son los peores: llegan con `status` STRING (FETCH_ERROR,
      // TIMEOUT_ERROR...), asi que ninguna comparacion numerica los atrapaba, y su payload no trae
      // `data`, asi que reutilizar `data.detail` habria dejado el mensaje vacio.
      if (necesitaAvisoGenerico(response as PayloadDeFallo)) {
        const endpoint = endpointDeLaAccion(action)

        if (!endpoint || !ENDPOINTS_CON_AVISO_PROPIO.has(endpoint)) {
          store.dispatch(addNotification({ value: avisoDelFalloRtk(response as PayloadDeFallo) }))
        }
      }

      return next(action)
    }

    if (isFulfilled(action)) {
      const cuerpo = action.payload as CuerpoConAviso
      const mensaje = cuerpo?.data?.detail

      // Dos condiciones que faltaban:
      //
      // - Solo mutaciones. Antes tambien entraban las CONSULTAS, asi que un GET cuyo cuerpo trajera
      //   `status: 200` sacaba un toast verde sin que el usuario hubiera hecho nada.
      // - Solo si hay mensaje. `data.detail` es opcional en la practica, y sin el se despachaba un
      //   aviso con el cuerpo vacio: un recuadro verde que no dice nada.
      //
      // Queda simetrico con la rama de fallo, que ya filtraba por endpoint. Lo habia dejado
      // señalado a proposito en el PR 18329 para no mezclar las dos mitades en un mismo cambio.
      if (cuerpo?.status === 200 && esMutacion(action) && mensaje?.trim()) {
        store.dispatch(
          addNotification({
            value: {
              message: mensaje,
              style: 'success',
              title: cuerpo?.data?.title ?? 'Reservas',
            },
          }),
        )
      }
    }

    return next(action)
  }
